// HOME-DASH (Andrei, 2026-10-27): Home is a widget grid each person arranges — show or hide,
// reorder, resize — saved per person in `me/prefs/home_layout` (contracts/api/me.ts). This file is
// the pure model: the widget catalogue in its default order and widths, the merge of a stored
// layout over it (an unknown id is dropped, a widget the stored layout leaves out takes its
// default place), and the edits. "Reset to default" saves `{}`.
import type { DashboardSize } from "@companyos/ui";
import type { HomeLayoutPref } from "../work/task-page/queries";

export type WidgetId =
  | "kpi_score"
  | "goal_score"
  | "cost_week"
  | "agent_runtime"
  | "human_hours"
  | "setup"
  | "pinned"
  | "needs_you"
  | "my_work"
  | "live_activity"
  | "numbers_due"
  | "assess"
  | "scorecard"
  | "my_scorecard"
  | "recent"
  | "team_scorecards"
  | "friday_report"
  | "focus";

export type WidgetDef = {
  id: WidgetId;
  title: string;
  size: DashboardSize;
  /** Off until the person shows it. */
  hidden?: boolean;
};

/**
 * The default layout, in order. Twelve columns: the four small numbers fill the first row, the
 * setup checklist the second (while it lasts), then three lists, then the scorecard and Recent.
 */
export const WIDGETS: readonly WidgetDef[] = [
  { id: "kpi_score", title: "KPI score", size: "xs" },
  { id: "goal_score", title: "Goal score", size: "xs" },
  { id: "cost_week", title: "Cost (week)", size: "xs" },
  { id: "agent_runtime", title: "Agent runtime (week)", size: "l" },
  // Andrei, 2026-10-27: "I don't think we should track human time yet unless we integrate
  // WorkPulse" — kept, hidden by default.
  { id: "human_hours", title: "Human hours (needs WorkPulse)", size: "xs", hidden: true },
  { id: "setup", title: "Setup checklist", size: "full" },
  // ADMIN-INSIGHTS: answers pinned from Ask (owners and admins; absent while none is pinned).
  { id: "pinned", title: "Pinned answers", size: "full" },
  { id: "needs_you", title: "Needs you", size: "m" },
  { id: "my_work", title: "My work", size: "m" },
  { id: "live_activity", title: "Live activity", size: "m" },
  { id: "numbers_due", title: "Numbers due", size: "m" },
  { id: "assess", title: "Assessments", size: "m" },
  { id: "scorecard", title: "Scorecard", size: "xl" },
  { id: "my_scorecard", title: "My scorecard", size: "xl" },
  { id: "recent", title: "Recent", size: "m" },
  { id: "team_scorecards", title: "Team scorecards", size: "m" },
  { id: "friday_report", title: "Friday report", size: "m" },
  { id: "focus", title: "Focus", size: "m" },
];

const BY_ID = new Map<string, WidgetDef>(WIDGETS.map((w) => [w.id, w]));

export type LayoutEntry = { id: WidgetId; title: string; size: DashboardSize; hidden: boolean };

const fromDef = (w: WidgetDef): LayoutEntry => ({
  id: w.id,
  title: w.title,
  size: w.size,
  hidden: w.hidden === true,
});

/**
 * The layout to draw: the stored widgets in their order, then the catalogue's widgets the
 * stored layout does not name, each at its default place relative to the one before it.
 */
export function resolveLayout(stored: HomeLayoutPref | null | undefined): LayoutEntry[] {
  const out: LayoutEntry[] = [];
  const seen = new Set<string>();
  for (const s of stored?.widgets ?? []) {
    const def = BY_ID.get(s.id);
    if (!def || seen.has(def.id)) continue;
    seen.add(def.id);
    out.push({
      id: def.id,
      title: def.title,
      size: s.size ?? def.size,
      hidden: s.hidden ?? def.hidden === true,
    });
  }
  WIDGETS.forEach((def, i) => {
    if (seen.has(def.id)) return;
    // After the nearest earlier catalogue widget already placed (else first).
    const before = WIDGETS.slice(0, i)
      .reverse()
      .find((w) => seen.has(w.id));
    const at = before ? out.findIndex((e) => e.id === before.id) + 1 : 0;
    out.splice(at, 0, fromDef(def));
    seen.add(def.id);
  });
  return out;
}

/** The value to save: every widget in order, with what differs from its default. */
export function toPref(layout: readonly LayoutEntry[]): HomeLayoutPref {
  return {
    widgets: layout.map((e) => {
      const def = BY_ID.get(e.id);
      return {
        id: e.id,
        ...(e.hidden !== (def?.hidden === true) ? { hidden: e.hidden } : {}),
        ...(e.size !== def?.size ? { size: e.size } : {}),
      };
    }),
  };
}

/**
 * Moves `id` to position `index` among `shown` (the widgets on screen, in order); the widgets
 * not on screen (hidden, or with nothing to show for this person) keep their slots.
 */
export function moveWidget(
  layout: readonly LayoutEntry[],
  shown: readonly string[],
  id: string,
  index: number,
): LayoutEntry[] {
  const from = shown.indexOf(id);
  if (from < 0 || index < 0 || index >= shown.length || from === index) return [...layout];
  const order = [...shown];
  order.splice(from, 1);
  order.splice(index, 0, id);
  const slots = new Set(shown);
  const byId = new Map(layout.map((e) => [e.id as string, e]));
  let k = 0;
  return layout.map((e) => {
    if (!slots.has(e.id)) return e;
    const next = byId.get(order[k++] ?? "");
    return next ?? e;
  });
}

export function resizeWidget(
  layout: readonly LayoutEntry[],
  id: string,
  size: DashboardSize,
): LayoutEntry[] {
  return layout.map((e) => (e.id === id ? { ...e, size } : e));
}

export function setHidden(
  layout: readonly LayoutEntry[],
  id: string,
  hidden: boolean,
): LayoutEntry[] {
  return layout.map((e) => (e.id === id ? { ...e, hidden } : e));
}
